// Day Book — every voucher in a period with full-text search + filters (Phase 5.7).
// Date range (single day, week, month, FY-to-date), base-type pill filter, party
// dropdown, amount range, full-text search, include-cancelled toggle, pagination.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TableSkeleton } from '@/components/primitives/Skeleton';
import { api } from '@/lib/api';
import { Money } from '@/components/primitives/Money';
import { KbdChip } from '@/components/primitives/KbdChip';
import { Button } from '@/components/primitives/Button';
import { ledgers } from '@/api/masters';
import { fiscalYearForDate, formatDateDDMMMYYYY, displayNarration, todayIso as todayISO, fyStartIso as fyStart, daysAgoIso as daysAgo } from '@/utils/format';
import { cn } from '@/lib/cn';
import { ExportCsvButton } from '@/components/ExportCsvButton';
import { PageHeader, HeaderBadge } from '@/components/primitives/PageHeader';

type BaseType = 'SALES' | 'PURCHASE' | 'RECEIPT' | 'PAYMENT' | 'CONTRA' | 'JOURNAL' | 'CREDIT_NOTE' | 'DEBIT_NOTE';

interface DayBookRow {
  id?: string;
  voucherNo: string;
  voucherType: string;
  voucherTypeName?: string | null;
  baseTypeCode?: string | null;
  party: string;
  debit: number;
  credit: number;
  narration?: string;
  totalAmount?: number;
  cancelledAt?: string | null;
  date?: string;
}
interface DayBookResponse {
  dateRange: { from: string | null; to: string | null };
  rows: DayBookRow[];
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
  totals: { debit: number; credit: number; voucherCount: number; currency: string };
  needsCompanyPick?: boolean;
  message?: string;
}

const BASE_TYPES: Array<{ code: BaseType | 'ALL'; label: string }> = [
  { code: 'ALL',         label: 'All'      },
  { code: 'SALES',       label: 'Sales'    },
  { code: 'PURCHASE',    label: 'Purchase' },
  { code: 'RECEIPT',     label: 'Receipt'  },
  { code: 'PAYMENT',     label: 'Payment'  },
  { code: 'CONTRA',      label: 'Contra'   },
  { code: 'JOURNAL',     label: 'Journal'  },
  { code: 'CREDIT_NOTE', label: 'CN'       },
  { code: 'DEBIT_NOTE',  label: 'DN'       },
];

const RANGE_PRESETS: Array<{ key: string; label: string; range: () => { from: string; to: string } }> = [
  { key: 'today',  label: 'Today',  range: () => ({ from: todayISO(), to: todayISO() }) },
  { key: '7d',     label: '7d',     range: () => ({ from: daysAgo(6), to: todayISO() }) },
  { key: '30d',    label: '30d',    range: () => ({ from: daysAgo(29), to: todayISO() }) },
  { key: 'fy',     label: 'FY-TD',  range: () => ({ from: fyStart(), to: todayISO() }) },
];

export function DayBookPage() {
  const [fromDate, setFromDate] = useState<string>(todayISO());
  const [toDate, setToDate] = useState<string>(todayISO());
  const [baseType, setBaseType] = useState<BaseType | 'ALL'>('ALL');
  const [partyLedgerId, setPartyLedgerId] = useState<string>('');
  const [searchQ, setSearchQ] = useState<string>('');
  const [minAmount, setMinAmount] = useState<string>('');
  const [maxAmount, setMaxAmount] = useState<string>('');
  const [includeCancelled, setIncludeCancelled] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const pageSize = 50;

  const ledgerListQ = ledgers.useList({ pageSize: 500 });
  const partyLedgerOptions = (ledgerListQ.data?.rows ?? []).filter((l: any) => {
    const bl = l.group?.behavesLike;
    return bl === 'SUNDRY_DEBTORS' || bl === 'SUNDRY_CREDITORS' || l.billByBill === true;
  });

  const dayBook = useQuery<DayBookResponse>({
    queryKey: ['reports', 'day-book', fromDate, toDate, baseType, partyLedgerId, searchQ, minAmount, maxAmount, includeCancelled, page],
    queryFn: async () => {
      const params: Record<string, string | number> = { fromDate, toDate, page, pageSize };
      if (baseType !== 'ALL') params.baseTypeCode = baseType;
      if (partyLedgerId) params.partyLedgerId = partyLedgerId;
      if (searchQ.trim()) params.q = searchQ.trim();
      if (minAmount) params.minAmount = minAmount;
      if (maxAmount) params.maxAmount = maxAmount;
      if (includeCancelled) params.includeCancelled = 'true';
      const res = await api.get<DayBookResponse>('/reports/day-book', { params });
      return res.data;
    },
    placeholderData: (prev) => prev,
    staleTime: 15_000,
  });

  function applyPreset(r: { from: string; to: string }) { setFromDate(r.from); setToDate(r.to); setPage(1); }

  async function downloadCsv() {
    // Pull all pages of the current filter result, not just the visible page.
    const all: DayBookRow[] = [];
    let p = 1;
    while (true) {
      const params: Record<string, string | number> = { fromDate, toDate, page: p, pageSize: 200 };
      if (baseType !== 'ALL') params.baseTypeCode = baseType;
      if (partyLedgerId) params.partyLedgerId = partyLedgerId;
      if (searchQ.trim()) params.q = searchQ.trim();
      if (minAmount) params.minAmount = minAmount;
      if (maxAmount) params.maxAmount = maxAmount;
      if (includeCancelled) params.includeCancelled = 'true';
      const res = await api.get<DayBookResponse>('/reports/day-book', { params });
      all.push(...(res.data.rows ?? []));
      if (!res.data.pagination || p >= res.data.pagination.totalPages || all.length >= 5000) break;
      p++;
    }
    const header = ['Date','Voucher No.','Type','Party','Narration','Debit','Credit','Total','Cancelled'];
    const csv = [header, ...all.map((r) => [
      r.date ? r.date.slice(0, 10) : '',
      r.voucherNo, r.voucherType,
      r.party ?? '', (r.narration ?? '').replace(/\n/g, ' '),
      r.debit.toFixed(2), r.credit.toFixed(2),
      (r.totalAmount ?? 0).toFixed(2),
      r.cancelledAt ? 'YES' : '',
    ])].map((row) => row.map((c) => {
      const s = String(c);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');
    // BOM so Excel opens as UTF-8 not Latin-1
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `day-book-${fromDate}-to-${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }
  function resetFilters() {
    setBaseType('ALL'); setPartyLedgerId(''); setSearchQ(''); setMinAmount(''); setMaxAmount(''); setIncludeCancelled(false); setPage(1);
  }
  const hasFilters = baseType !== 'ALL' || partyLedgerId || searchQ.trim() || minAmount || maxAmount || includeCancelled;

  const d = dayBook.data;

  return (
    <div className="min-h-screen bg-snow">
      <PageHeader
        backHref="/reports"
        backLabel="Reports"
        title="Day Book"
        badge={<HeaderBadge>F1</HeaderBadge>}
        actions={
          <>
            <div className="flex items-center gap-1 rounded-md border border-mist bg-frost p-1">
              {RANGE_PRESETS.map((p) => (
                <button key={p.key} type="button" onClick={() => applyPreset(p.range())}
                  className="rounded-sm px-2 py-0.5 text-caption text-slate hover:bg-haze">
                  {p.label}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-2 text-caption text-slate">
              From <input type="date" value={fromDate} max={toDate} onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
                className="rounded-sm border border-mist bg-haze px-2 py-1 text-body-sm focus:border-cobalt/60 focus:outline-none focus:shadow-focus" />
            </label>
            <label className="flex items-center gap-2 text-caption text-slate">
              To <input type="date" value={toDate} min={fromDate} max={todayISO()} onChange={(e) => { setToDate(e.target.value); setPage(1); }}
                className="rounded-sm border border-mist bg-haze px-2 py-1 text-body-sm focus:border-cobalt/60 focus:outline-none focus:shadow-focus" />
            </label>
            <div className="rounded-md border border-mist bg-haze px-3 py-1 text-body-sm">
              <div className="text-micro text-cool uppercase tracking-[0.12em]">FY</div>
              <div className="font-mono text-obsidian">{fiscalYearForDate(new Date(fromDate))}</div>
            </div>
          </>
        }
      />
      <div className="sticky top-0 z-10 border-b border-mist bg-snow/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1400px] flex-wrap gap-2 px-6 py-3">
          <div className="flex flex-wrap gap-1">
            {BASE_TYPES.map((b) => (
              <button key={b.code} type="button" onClick={() => { setBaseType(b.code); setPage(1); }}
                className={cn('rounded-sm px-2 py-0.5 text-caption transition',
                  baseType === b.code ? 'bg-cobalt text-snow' : 'text-cool hover:bg-haze')}>
                {b.label}
              </button>
            ))}
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <input value={searchQ} onChange={(e) => { setSearchQ(e.target.value); setPage(1); }}
              placeholder="Search number, narration, party…"
              className="w-64 rounded-sm border border-mist bg-haze px-2 py-1 text-body-sm focus:border-cobalt/60 focus:outline-none focus:shadow-focus" />
            <select value={partyLedgerId} onChange={(e) => { setPartyLedgerId(e.target.value); setPage(1); }}
              className="rounded-sm border border-mist bg-haze px-2 py-1 text-body-sm focus:border-cobalt/60 focus:outline-none focus:shadow-focus">
              <option value="">All parties</option>
              {partyLedgerOptions.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            <input value={minAmount} onChange={(e) => { setMinAmount(e.target.value); setPage(1); }}
              placeholder="Min ₹" type="number" min={0}
              className="w-24 rounded-sm border border-mist bg-haze px-2 py-1 text-body-sm focus:border-cobalt/60 focus:outline-none focus:shadow-focus" />
            <input value={maxAmount} onChange={(e) => { setMaxAmount(e.target.value); setPage(1); }}
              placeholder="Max ₹" type="number" min={0}
              className="w-24 rounded-sm border border-mist bg-haze px-2 py-1 text-body-sm focus:border-cobalt/60 focus:outline-none focus:shadow-focus" />
            <label className="flex items-center gap-1 text-caption text-slate">
              <input type="checkbox" checked={includeCancelled} onChange={(e) => { setIncludeCancelled(e.target.checked); setPage(1); }} />
              Cancelled
            </label>
            {hasFilters && <Button variant="ghost" onClick={resetFilters}>Reset</Button>}
            <ExportCsvButton onClick={downloadCsv} disabled={!d || d.rows.length === 0} />
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-[1400px] px-6 py-6">
        <section className="orz-stagger mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Stat label="Vouchers" value={d?.totals.voucherCount ?? 0} />
          <Stat label="Total debit" amount={d?.totals.debit ?? 0} important />
          <Stat label="Total credit" amount={d?.totals.credit ?? 0} important />
        </section>

        {d?.needsCompanyPick && (
          <section className="mb-6 rounded-md border border-cobalt/40 bg-cobalt/[0.05] p-5">
            <h2 className="font-display text-display-sm font-semibold text-obsidian">Pick a company first</h2>
            <p className="mt-1 text-body-md text-slate">Use the company picker on the Gateway.</p>
          </section>
        )}

        <section>
          <div className="overflow-hidden rounded-md border border-mist bg-frost">
            <div className="overflow-x-auto">
              <div className="grid min-w-max grid-cols-[110px_90px_120px_220px_minmax(280px,1fr)_120px_130px_130px] gap-x-3 border-b border-mist bg-frost px-4 py-2 text-micro font-medium uppercase tracking-[0.12em] text-cool">
                <div>Date</div>
                <div>Type</div>
                <div>Voucher</div>
                <div>Party</div>
                <div>Narration</div>
                <div className="text-right">Total</div>
                <div className="text-right">Debit</div>
                <div className="text-right">Credit</div>
              </div>
              {dayBook.isLoading ? (
                <TableSkeleton rows={8} />
              ) : !d || d.rows.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
                  <p className="text-body-md text-slate">No vouchers match these filters.</p>
                  {hasFilters && <p className="text-caption text-cool">Try resetting filters or widening the date range.</p>}
                </div>
              ) : (
                d.rows.map((r) => (
                  <a key={r.id ?? r.voucherNo} href={r.id ? `/vouchers/${r.id}` : '#'}
                    className={cn('grid min-w-max grid-cols-[110px_90px_120px_220px_minmax(280px,1fr)_120px_130px_130px] gap-x-3 border-b border-mist/50 px-4 py-2 text-body-sm transition hover:bg-cobalt/[0.05]',
                      r.cancelledAt && 'opacity-50 line-through')}>
                    <div className="tabular-nums text-cool">{r.date ? formatDateDDMMMYYYY(new Date(r.date)) : ''}</div>
                    <div>
                      <span className="inline-flex items-center h-5 px-2 rounded-sm bg-cobalt/10 text-cobalt text-nano font-medium uppercase tracking-[0.1em]">{r.voucherType}</span>
                    </div>
                    <div className="font-mono text-cobalt truncate">{r.voucherNo}</div>
                    <div className="truncate text-obsidian" title={r.party}>{r.party || '—'}</div>
                    <div className="truncate text-cool" title={displayNarration(r.narration)}>{displayNarration(r.narration) || '—'}</div>
                    <div className="text-right font-mono tabular-nums text-obsidian">
                      {r.totalAmount != null ? r.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '—'}
                    </div>
                    <div className="text-right font-mono tabular-nums text-obsidian">
                      {r.debit > 0 ? r.debit.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : <span className="text-cool">—</span>}
                    </div>
                    <div className="text-right font-mono tabular-nums text-obsidian">
                      {r.credit > 0 ? r.credit.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : <span className="text-cool">—</span>}
                    </div>
                  </a>
                ))
              )}
            </div>
            {d && d.pagination.totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-mist bg-frost px-4 py-2 text-caption text-slate">
                <div>
                  Page {d.pagination.page} of {d.pagination.totalPages} · {d.pagination.total} vouchers
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</Button>
                  <Button variant="ghost" disabled={page >= d.pagination.totalPages} onClick={() => setPage(page + 1)}>Next</Button>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>

      <footer className="fixed bottom-0 inset-x-0 z-10 border-t border-mist bg-snow/95 backdrop-blur print:hidden">
        <div className="mx-auto flex max-w-[1400px] items-center gap-x-5 gap-y-1 overflow-x-auto px-6 py-2 text-caption text-slate">
          <span className="flex items-center gap-2"><KbdChip>F1</KbdChip> Day Book</span>
          <span className="flex items-center gap-2"><KbdChip>F2</KbdChip> Change Date</span>
          <span className="flex items-center gap-2"><KbdChip>Ctrl K</KbdChip> Command palette</span>
          <span className="flex items-center gap-2"><KbdChip>Esc</KbdChip> Back</span>
        </div>
      </footer>
    </div>
  );
}

function Stat({ label, value, amount, important }: { label: string; value?: number; amount?: number; important?: boolean }) {
  return (
    <div className="orz-lift rounded-md border border-mist bg-frost p-4" style={{ backgroundImage: 'var(--wash-card)' }}>
      <div className="text-micro font-medium uppercase tracking-[0.12em] text-cool">{label}</div>
      <div className="mt-2">
        {amount !== undefined ? (
          <Money amount={amount} important={important} className="text-display-md" />
        ) : (
          <span className="font-mono text-display-md text-obsidian">{(value ?? 0).toLocaleString('en-IN')}</span>
        )}
      </div>
    </div>
  );
}
